import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { fetchWithAuth } from "../Context/UserContext";
import PageLayout from "../components/layout/PageLayout";
import API_URL from "../config";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const ChangePassword = () => {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPassword, setShowPassword] = useState({
    current_password: false,
    new_password: false,
    confirm_password: false,
  });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const resetToken = queryParams.get("token");
  const authToken = localStorage.getItem("access_token");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleShowPassword = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field], // ✅ Asegura que solo cambia la visibilidad del campo específico
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      setError("Las contraseñas no coinciden");
      return;
    }

    try {
      const body = resetToken
        ? { new_password: form.new_password, token: resetToken } // Caso 1: Restablecimiento
        : {
            current_password: form.current_password,
            new_password: form.new_password,
          }; // Caso 2: Usuario autenticado

      const headers = { "Content-Type": "application/json" };

      let response;

      if (resetToken) {
        // 🔹 Si hay resetToken, es un restablecimiento → fetch sin autenticación
        response = await fetch(`${API_URL}/user/changepassword`, {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });
      } else {
        // 🔹 Si no hay resetToken, es un cambio con usuario autenticado → fetchWithAuth
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }
        response = await fetchWithAuth(`${API_URL}/user/changepassword`, {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });
      }

      const data = await response.json();
      console.log(data);

      if (response.ok) {
        setMessage(data.message);
        setTimeout(() => {
          navigate("/login");
        }, 500);
      } else if (response.status === 401) {
        setError(
          "El enlace ha expirado o es inválido. Solicita un nuevo correo."
        );
        setTimeout(() => {
          navigate("/forgetpassword"); // Redirigir al usuario para solicitar otro link
        }, 2000);
      } else {
        setError(
          typeof data.error === "string"
            ? data.error
            : "Ocurrió un error inesperado."
        );
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
      setError("Error en la solicitud. Inténtalo de nuevo más tarde.");
    }
  };

  return (
    <PageLayout>
      <div className="flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <h2 className="text-center text-2xl font-semibold text-foreground">Cambiar Contraseña</h2>

            <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              {!resetToken && (
                <div>
                  <label
                    htmlFor="current_password"
                    className="mb-1.5 block text-sm font-medium text-foreground"
                  >
                    Contraseña Actual
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword.current_password ? "text" : "password"}
                      name="current_password"
                      value={form.current_password}
                      onChange={handleChange}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowPassword("current_password")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword.current_password ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="new_password" className="mb-1.5 block text-sm font-medium text-foreground">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPassword.new_password ? "text" : "password"}
                    name="new_password"
                    value={form.new_password}
                    onChange={handleChange}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword("new_password")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword.new_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirm_password"
                  className="mb-1.5 block text-sm font-medium text-foreground"
                >
                  Confirmar Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPassword.confirm_password ? "text" : "password"}
                    name="confirm_password"
                    value={form.confirm_password}
                    onChange={handleChange}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword("confirm_password")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword.confirm_password ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full">
                Cambiar Contraseña
              </Button>
              {message && <p className="text-center text-sm font-semibold text-green-600">{message}</p>}
              {error && <p className="text-center text-sm font-semibold text-destructive">{error}</p>}
            </form>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default ChangePassword;
